import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { revenueReport } from '../api/dashboard';
import { revenueMonthly } from '../api/kpi';
import { activeAnnouncements } from '../api/announcements';
import RevenueChart from '../components/RevenueChart';
import Avatar from '../components/Avatar';
import { useModal } from '../components/ModalProvider';
import { useToast } from '../components/Toast';
import FaceAttendanceModal from '../components/FaceAttendanceModal';
import MobileDashboard from './MobileDashboard';
import { fmtDate, fmtDateTime, fmtVND, roleName } from '../utils/format';

// Trang chủ MOBILE (<=768px) có bố cục riêng theo thiết kế; desktop giữ nguyên dashboard cũ.
function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return m;
}

const SALE_TOP = 5; // Trang chủ: gọn — chỉ top 5 (chi tiết đầy đủ xem ở Báo cáo kinh doanh)
const CUST_TOP = 5; // Trang chủ: gọn — chỉ top 5 (chi tiết đầy đủ xem ở Doanh thu lũy kế)

export default function DashboardPage() {
  const { me } = useAuth();
  const { openModal, closeModal } = useModal();
  const toast = useToast();
  const isMobile = useIsMobile();
  const canAttendance = me.role === 'NV' || me.role === 'TP';
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthly, setMonthly] = useState(null);
  const [chartTab, setChartTab] = useState('company'); // tab biểu đồ: 'company' | groupId
  const [chartYear, setChartYear] = useState(null); // null = năm hiện tại; số = xem năm cũ
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    revenueReport().then(setReport).finally(() => setLoading(false));
    activeAnnouncements().then(setNotifs).catch(() => {});
  }, []);

  useEffect(() => {
    revenueMonthly(chartYear).then(setMonthly).catch(() => setMonthly(null));
  }, [chartYear]);

  const CHART_COLORS = ['#36d399', '#5b8cff', '#fbbf24', '#f87171', '#a78bfa', '#22d3ee', '#fb923c', '#4ade80'];
  const chartTabs =
    monthly && monthly.months?.length
      ? [
          {
            key: 'company',
            name: monthly.scope === 'company' ? 'Toàn công ty' : monthly.scope === 'group' ? 'Nhóm của bạn' : 'Cá nhân',
            values: monthly.company,
            color: '#7C3AED',
          },
          ...(monthly.scope === 'company'
            ? (monthly.groups || []).map((g, i) => ({
                key: g.id,
                name: g.name,
                values: g.values,
                color: CHART_COLORS[(i + 1) % CHART_COLORS.length],
              }))
            : []),
        ]
      : [];
  const activeTab = chartTabs.find((t) => t.key === chartTab) || chartTabs[0];
  const activeTotal = activeTab ? activeTab.values.reduce((a, v) => a + (Number(v) || 0), 0) : 0;

  const bySale = report?.bySale || [];
  const byCustomer = report?.byCustomer || [];
  const saleMax = Math.max(1, ...bySale.map((s) => Number(s.revenuePersonal ?? s.revenue) || 0));
  const custMax = Math.max(1, ...byCustomer.map((c) => Number(c.revenue) || 0));

  if (isMobile) return <MobileDashboard />;

  return (
    <>
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2>Trang chủ</h2>
          <div className="sub">
            Xin chào {me.name} · {roleName(me.displayRole || me.role)}
            {me.group ? ` · ${me.group.name}` : ''}
          </div>
        </div>
        {canAttendance && (
          <button
            className="btn"
            onClick={() => openModal(<FaceAttendanceModal onClose={closeModal} toast={toast} />)}
            style={{ fontSize: 16, fontWeight: 700, padding: '12px 22px', borderRadius: 12, boxShadow: '0 4px 14px color-mix(in srgb, var(--accent) 35%, transparent)' }}
          >
            📷 Chấm công
          </button>
        )}
      </div>

      {/* Biểu đồ đường doanh thu theo tháng/quý/năm */}
      <div className="card">
        <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>📈 Doanh thu {monthly?.year ? `· ${monthly.year}` : ''}</h3>
            {monthly?.years?.length > 1 && (
              <select
                value={monthly.year || ''}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  setChartYear(y === new Date().getFullYear() ? null : y);
                }}
                style={{ width: 'auto' }}
              >
                {[...monthly.years].sort((a, b) => b - a).map((y) => (
                  <option key={y} value={y}>Năm {y}</option>
                ))}
              </select>
            )}
            {monthly?.historical && <span className="chip">Số cố định (đã chốt từ lũy kế)</span>}
          </div>
        </div>

        {chartTabs.length > 1 && (
          <div className="seg" style={{ marginBottom: 14, maxWidth: '100%' }}>
            {chartTabs.map((t) => (
              <button key={t.key} className={chartTab === t.key ? 'active' : ''} onClick={() => setChartTab(t.key)}>
                {t.name}
              </button>
            ))}
          </div>
        )}

        {!activeTab || activeTotal <= 0 ? (
          <div className="empty">
            Chưa có dữ liệu doanh thu theo tháng. {me.role === 'GD' ? 'Vào tab Đồng bộ → bấm “Backfill KPI (từ đầu năm)” để lấy dữ liệu các tháng từ SocSon.' : 'Dữ liệu sẽ hiển thị sau khi Giám đốc đồng bộ.'}
          </div>
        ) : (
          <RevenueChart months={monthly.months} values={activeTab.values} color="#7C3AED" name={activeTab.name} />
        )}

        {activeTab && activeTotal > 0 && (
          <div className="small muted" style={{ marginTop: 10 }}>
            Tổng năm ({activeTab.name}): <b style={{ color: 'var(--accent2)' }}>{fmtVND(activeTotal)}</b>
          </div>
        )}
      </div>

      {loading ? (
        <div className="card"><div className="empty">Đang tải…</div></div>
      ) : !report ? (
        <div className="card">
          <div className="empty">
            Chưa có dữ liệu báo cáo doanh thu. Hãy đẩy báo cáo từ extension (tab Báo cáo doanh thu → Đẩy doanh thu sang funnytask).
          </div>
        </div>
      ) : (
        <>
          {report.syncedAt && (
            <div className="small muted" style={{ marginBottom: 12 }}>
              Cập nhật lúc {fmtDate(report.syncedAt)} · Doanh thu = MH + VC (cước + phụ thu) · số <span style={{ color: '#16a34a', fontWeight: 600 }}>(xanh)</span> = doanh thu hỗ trợ
            </div>
          )}

          {/* Doanh thu theo nhân viên / khách hàng */}
          <div className="rev-cols">
            <div className="card" style={{ marginBottom: 0 }}>
              <div className="row between" style={{ marginBottom: 6 }}>
                <h3 style={{ margin: 0, fontSize: 13 }}>Doanh thu theo nhân viên</h3>
              </div>
              {bySale.length ? (
                bySale.slice(0, SALE_TOP).map((s, i) => {
                  const val = Number(s.revenuePersonal ?? s.revenue) || 0;
                  // Phần doanh thu HỖ TRỢ nằm trong DT cá nhân (nhận + tự làm GTS + đi hỗ trợ nhóm khác).
                  const sup = (Number(s.supportCredit) || 0) + (Number(s.supportSelf) || 0) + (Number(s.supportHandled) || 0);
                  return (
                    <div className="dash-list-row" key={s.sale_code || s.name || i}>
                      <Avatar name={s.name} size={26} />
                      <div className="dash-list-main">
                        <div className="dash-list-name">{s.name}</div>
                        <div className="dash-progress"><div style={{ width: `${Math.round((val / saleMax) * 100)}%` }} /></div>
                      </div>
                      <div className="dash-list-val">
                        {fmtVND(val)}
                        {sup > 0 && <span style={{ color: '#16a34a', fontWeight: 600 }}> ({fmtVND(sup)})</span>}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty">Không có dữ liệu</div>
              )}
            </div>

            <div className="card" style={{ marginBottom: 0 }}>
              <div className="row between" style={{ marginBottom: 6 }}>
                <h3 style={{ margin: 0, fontSize: 13 }}>Doanh thu theo khách hàng</h3>
              </div>
              {byCustomer.length ? (
                byCustomer.slice(0, CUST_TOP).map((c, i) => {
                  const val = Number(c.revenue) || 0;
                  return (
                    <div className="dash-list-row" key={c.customer_no || c.name || i}>
                      <div className="dash-list-main">
                        <div className="dash-list-name">{c.name || c.customer_no || '—'}</div>
                        <div className="dash-progress"><div style={{ width: `${Math.round((val / custMax) * 100)}%` }} /></div>
                      </div>
                      <div className="dash-list-val">{fmtVND(val)}</div>
                    </div>
                  );
                })
              ) : (
                <div className="empty">Không có dữ liệu</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Thông báo — dữ liệu thật từ mục Thông báo */}
      <div className="card" style={{ marginBottom: 0 }}>
        <h3 style={{ margin: '0 0 10px' }}>🔔 Thông báo</h3>
        {notifs.length ? (
          <div className="dash-notifs">
            {notifs.slice(0, 4).map((n) => (
              <div className="dash-notif" key={n._id || n.id}>
                <div className="dash-notif-ico">📢</div>
                <div>
                  <div className="dash-notif-text">{n.title || n.content}</div>
                  <div className="dash-notif-time">{fmtDateTime(n.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty">Chưa có thông báo mới.</div>
        )}
      </div>
    </>
  );
}
